<template>
  <div class="word-trainer">
    <div v-if="!started" class="setup">
      <h2>Чтение слов</h2>

      <div class="setup-section">
        <div class="setup-label">Режим:</div>
        <div class="setup-options">
          <button
            :class="['opt-btn mode-words', { active: mode === 'words' }]"
            @click="mode = 'words'"
          >
            Слова
          </button>
          <button
            v-for="lvl in sentenceLevels"
            :key="'s' + lvl"
            :class="['opt-btn mode-sentence', { active: mode === 'sentence-' + lvl }]"
            @click="mode = 'sentence-' + lvl"
          >
            Предл. {{ lvl }}
          </button>
          <button
            :class="['opt-btn mode-story', { active: mode === 'story' }]"
            @click="mode = 'story'"
          >
            Рассказы
          </button>
        </div>
      </div>

      <div class="setup-section">
        <div class="setup-label">Время:</div>
        <div class="setup-options">
          <button
            v-for="m in durations"
            :key="m"
            :class="['opt-btn', { active: chosenMinutes === m }]"
            @click="chosenMinutes = m"
          >
            {{ m }} мин
          </button>
        </div>
      </div>

      <div class="setup-section">
        <div class="setup-label">Отображение:</div>
        <div class="setup-options">
          <button
            :class="['opt-btn', { active: displayMode === 'whole' }]"
            @click="displayMode = 'whole'"
          >
            Целиком
          </button>
          <button
            :class="['opt-btn', { active: displayMode === 'syllable' }]"
            @click="displayMode = 'syllable'"
          >
            По слогам
          </button>
        </div>
      </div>

      <div v-if="loadError" class="load-error">{{ loadError }}</div>

      <button class="btn-start" :disabled="!chosenMinutes || !mode" @click="startGame">
        Начать
      </button>

      <div class="back-top">
        <router-link to="/">← На главную</router-link>
      </div>
    </div>

    <template v-else-if="!finished">
      <div class="word-display" :class="{ syllable: syllableMode }">
        <template v-if="syllableMode">
          <template v-for="(part, i) in currentParts" :key="i">
            <span v-if="i > 0" class="syllable-sep"> - </span>
            <span
              class="syllable-part"
              :class="{ highlight: highlightedPart === i }"
            >{{ part }}</span>
          </template>
        </template>
        <template v-else>
          {{ cleanWord }}
        </template>
      </div>

      <div class="bottom-bar">
        <div class="controls-bar">
          <div class="hint">
            <kbd>+</kbd> / <span class="swipe-hint">➡</span> правильно &nbsp; <kbd>−</kbd> / <span class="swipe-hint">⬅</span> ошибка
          </div>
          <button class="btn-syllable" @click="syllableMode = !syllableMode">
            {{ syllableMode ? 'Слово' : 'По слогам' }}
          </button>
        </div>

        <div class="counter">
          {{ wordIndex + 1 }} / {{ items.length }}
        </div>

        <div class="history">
          <span
            v-for="(item, i) in history"
            :key="i"
            class="history-chip"
            :style="{ backgroundColor: chipColor(item.errors) }"
          >
            {{ item.word.length > 12 ? item.word.slice(0, 10) + '…' : item.word }}
          </span>
        </div>
      </div>

      <div class="timer">{{ timerDisplay }}</div>
    </template>

    <div v-else class="results">
      <div class="results-hero">
        <div class="results-emoji">🎉</div>
        <h2 class="results-praise">Ты молодец!</h2>

        <div v-if="totalStars > 0" class="stars-section">
          <div class="stars-row">
            <span
              v-for="i in wordStars"
              :key="'w' + i"
              class="star star-word"
              :style="{ animationDelay: (i - 1) * 0.15 + 's' }"
            >⭐</span>
          </div>
          <div v-if="bonusStars > 0" class="stars-row">
            <span
              v-for="i in bonusStars"
              :key="'b' + i"
              class="star star-bonus"
              :style="{ animationDelay: (wordStars + i - 1) * 0.15 + 's' }"
            >🌟</span>
          </div>
        </div>

        <div class="stars-legend">
          <span v-if="wordStars">⭐ × {{ wordStars }} за слова</span>
          <span v-if="bonusStars">🌟 × {{ bonusStars }} за выдержку</span>
        </div>
      </div>

      <div class="results-details">
        <div class="results-stats" v-if="summary">
          <p>Пройдено: <strong>{{ summary.total }}</strong></p>
          <p>С первой попытки: <strong>{{ summary.perfect }}</strong></p>
          <p>С ошибками: <strong>{{ summary.withErrors }}</strong></p>
          <p v-if="summary.avgTime">Среднее время: <strong>{{ summary.avgTime }} сек</strong></p>
        </div>

        <div class="history final-history">
          <span
            v-for="(item, i) in history"
            :key="i"
            class="history-chip"
            :style="{ backgroundColor: chipColor(item.errors) }"
          >
            {{ item.word.length > 15 ? item.word.slice(0, 13) + '…' : item.word }}
          </span>
        </div>
      </div>

      <div class="results-actions">
        <button class="btn-start" @click="restart">Ещё раз</button>
        <router-link to="/" class="btn-home">На главную</router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useWordStats, loadWords, loadSentences, loadStories, splitToSyllables } from '../composables/useWordStats.js'
import { useSessionStats } from '../composables/useSessionStats.js'
import { useUserProfile } from '../composables/useUserProfile.js'
import Hammer from 'hammerjs'

const { record, getSessionSummary } = useWordStats()
const { startSession, endSession } = useSessionStats()
const { addStars } = useUserProfile()

const durations = [1, 3, 5, 7, 10]
const sentenceLevels = [1, 2, 3]

const mode = ref('words')
const chosenMinutes = ref(null)
const displayMode = ref('whole')
const started = ref(false)
const finished = ref(false)
const earlyFinish = ref(false)
const loadError = ref('')

const items = ref([])
const wordIndex = ref(0)
const history = ref([])
const syllableMode = ref(false)
const highlightedPart = ref(-1)
const currentErrors = ref(0)

const shownAt = ref(0)
const secondsLeft = ref(0)
let timerInterval = null
let activeSession = null

let hammer = null

const currentWord = computed(() => {
  if (wordIndex.value >= items.value.length) return ''
  return items.value[wordIndex.value]
})

const cleanWord = computed(() => {
  return currentWord.value.replace(/-/g, '')
})

const currentParts = computed(() => {
  if (!currentWord.value) return []
  return splitToSyllables(currentWord.value)
})

const timerDisplay = computed(() => {
  const m = Math.floor(secondsLeft.value / 60)
  const s = secondsLeft.value % 60
  return `${m}:${s.toString().padStart(2, '0')}`
})

const summary = computed(() => getSessionSummary(history.value))

const perfectCount = computed(() => history.value.filter(h => h.errors === 0).length)
const hasErrors = computed(() => history.value.some(h => h.errors > 0))
const wordStars = computed(() => Math.floor(perfectCount.value / 5))
const bonusStars = computed(() => {
  if (hasErrors.value) return 0
  return chosenMinutes.value ? Math.floor(chosenMinutes.value) : 0
})
const totalStars = computed(() => wordStars.value + bonusStars.value)

const sourceName = computed(() => {
  if (mode.value === 'words') return 'words'
  if (mode.value === 'story') return 'story'
  return 'sentence-' + mode.value.split('-')[1]
})

async function startGame() {
  if (!chosenMinutes.value || !mode.value) return
  loadError.value = ''

  try {
    if (mode.value === 'words') {
      const words = await loadWords('words.txt')
      items.value = shuffle(words)
    } else if (mode.value.startsWith('sentence-')) {
      const lvl = mode.value.split('-')[1]
      const sentences = await loadSentences(lvl)
      items.value = shuffle(sentences)
    } else if (mode.value === 'story') {
      const stories = await loadStories()
      items.value = shuffle(stories.map(s => s.text))
    }
  } catch (e) {
    loadError.value = 'Не удалось загрузить файл'
    return
  }

  if (!items.value.length) {
    loadError.value = 'Файл пуст'
    return
  }

  started.value = true
  finished.value = false
  earlyFinish.value = false
  history.value = []
  wordIndex.value = 0
  syllableMode.value = displayMode.value === 'syllable'
  highlightedPart.value = -1
  currentErrors.value = 0
  secondsLeft.value = chosenMinutes.value * 60
  shownAt.value = Date.now()
  activeSession = startSession('words', { mode: mode.value, time: chosenMinutes.value, display: displayMode.value })
  document.body.style.overflow = 'hidden'
  document.body.style.touchAction = 'none'
  const el = document.getElementById('app') || document.body
  hammer = new Hammer.Manager(el, { touchAction: 'none' })
  hammer.add(new Hammer.Swipe({ direction: Hammer.DIRECTION_HORIZONTAL, threshold: 5, velocity: 0.1 }))
  hammer.on('swipeleft', () => requestAnimationFrame(() => answerWrong()))
  hammer.on('swiperight', () => requestAnimationFrame(() => answerCorrect()))

  timerInterval = setInterval(() => {
    secondsLeft.value--
    if (secondsLeft.value <= 0) {
      clearInterval(timerInterval)
      endGame(false)
    }
  }, 1000)
}

function endGame(early) {
  finished.value = true
  earlyFinish.value = early
  window.removeEventListener('keydown', onKey)
  document.body.style.overflow = ''
  document.body.style.touchAction = ''
  if (hammer) { hammer.destroy(); hammer = null }
  if (timerInterval) clearInterval(timerInterval)
  addStars(totalStars.value)
  if (activeSession) {
    activeSession.total = history.value.length
    activeSession.correct = history.value.filter(h => h.errors === 0).length
    activeSession.wrong = history.value.filter(h => h.errors > 0).length
    const times = history.value.filter(h => h.time).map(h => h.time)
    if (times.length > 4) {
      const t = [...times].sort((a, b) => a - b).slice(2, -2)
      activeSession.avgTime = Math.round(t.reduce((s, v) => s + v, 0) / t.length / 100) / 10
    } else if (times.length) {
      activeSession.avgTime = Math.round(times.reduce((s, v) => s + v, 0) / times.length / 100) / 10
    }
    endSession(activeSession)
    activeSession = null
  }
}

function nextWord() {
  wordIndex.value++
  if (wordIndex.value >= items.value.length) {
    endGame(true)
    return
  }
  shownAt.value = Date.now()
  syllableMode.value = displayMode.value === 'syllable'
  highlightedPart.value = -1
  currentErrors.value = 0
}

function onKey(e) {
  if (!started.value || finished.value) return

  if (e.key === '+' || e.key === '=' || e.key === 'Equal') {
    answerCorrect()
  } else if (e.key === '-' || e.key === 'Minus') {
    answerWrong()
  } else if (e.key === 's' || e.key === 'S' || e.key === 'ы' || e.key === 'Ы') {
    syllableMode.value = !syllableMode.value
  }
}

function answerCorrect() {
  if (!started.value || finished.value) return
  const elapsed = Date.now() - shownAt.value
  const word = currentWord.value
  record(sourceName.value, word, true, elapsed)
  history.value.push({ word, errors: currentErrors.value, time: elapsed })
  nextWord()
}

function answerWrong() {
  if (!started.value || finished.value) return
  const elapsed = Date.now() - shownAt.value
  const word = currentWord.value
  record(sourceName.value, word, false, elapsed)
  currentErrors.value++
  syllableMode.value = true
  shownAt.value = Date.now()
}

function chipColor(errors) {
  if (errors === 0) return '#4caf50'
  if (errors === 1) return '#e91e90'
  if (errors === 2) return '#ff5252'
  return '#d32f2f'
}

function restart() {
  finished.value = false
  started.value = false
}

function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

onMounted(() => {
  window.addEventListener('keydown', onKey)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKey)
  document.body.style.overflow = ''
  document.body.style.touchAction = ''
  if (hammer) { hammer.destroy(); hammer = null }
  if (timerInterval) clearInterval(timerInterval)
})
</script>

<style scoped>
.word-trainer {
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 40px);
  padding-top: 20px;
}

.setup {
  padding-top: 20px;
}

.setup h2 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.setup-section {
  margin-bottom: 20px;
}

.setup-label {
  font-size: 16px;
  color: #555;
  font-weight: 700;
  margin-bottom: 8px;
}

.setup-options {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}

.opt-btn {
  padding: 10px 16px;
  border: 2px solid #ccc;
  border-radius: 10px;
  background: white;
  color: #555;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.opt-btn:hover {
  border-color: #e74c3c;
  color: #e74c3c;
}

.opt-btn.active {
  border-color: #e74c3c;
  background: #e74c3c;
  color: white;
}

.mode-words.active { background: #e74c3c; border-color: #e74c3c; }
.mode-sentence.active { background: #9b59b6; border-color: #9b59b6; }
.mode-story.active { background: #2c3e50; border-color: #2c3e50; }

.load-error {
  color: #e74c3c;
  font-size: 14px;
  margin-bottom: 12px;
}

.btn-start {
  padding: 14px 40px;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-start:hover {
  background: #c0392b;
}

.btn-start:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.back-top {
  margin-top: 16px;
}

.back-top a {
  color: #999;
  text-decoration: none;
  font-size: 14px;
}

.word-display {
  font-family: 'Nunito', 'Arial', sans-serif;
  font-size: 48px;
  font-weight: 400;
  color: #000;
  line-height: 1.2;
  margin: 50px 20px 20px;
  word-break: break-word;
  touch-action: none;
}

.word-display.syllable {
  font-size: 56px;
}

.syllable-sep {
  font-size: 0.5em;
  font-weight: 300;
  color: #999;
  touch-action: none;
}

.syllable-part {
  display: inline-block;
  transition: transform 0.2s, color 0.2s;
  margin: 0 2px;
  touch-action: none;
}

.syllable-part.highlight {
  transform: scale(1.15);
  color: #e74c3c;
}

.bottom-bar {
  margin-top: auto;
  padding-top: 20px;
}

.controls-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding: 0 16px;
}

.hint {
  font-size: 13px;
  color: #ccc;
}

.hint kbd {
  display: inline-block;
  padding: 1px 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
  background: #f7f7f7;
  font-family: monospace;
  font-size: 13px;
  color: #bbb;
}

.swipe-hint {
  font-size: 13px;
  color: #bbb;
}

.btn-syllable {
  padding: 6px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #f7f7f7;
  color: #999;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-syllable:hover {
  border-color: #999;
  color: #555;
}

.counter {
  font-size: 12px;
  color: #ccc;
  margin-bottom: 10px;
}

.history {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: center;
  max-height: 80px;
  overflow-y: auto;
  padding: 4px 8px 8px;
}

.final-history {
  max-height: 300px;
  margin-bottom: 20px;
}

.history-chip {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 5px;
  color: white;
  font-size: 12px;
  font-weight: 700;
  opacity: 0.7;
}

.timer {
  position: fixed;
  bottom: 10px;
  right: 14px;
  font-size: 11px;
  color: #ccc;
  font-family: monospace;
}

.results {
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.results-hero {
  text-align: center;
  margin-bottom: 24px;
}

.results-emoji {
  font-size: 64px;
  animation: heroEmojiIn 0.6s ease;
}

@keyframes heroEmojiIn {
  0% { transform: scale(0) rotate(-20deg); opacity: 0; }
  50% { transform: scale(1.3) rotate(10deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

.results-praise {
  font-size: 36px;
  font-weight: 900;
  background: linear-gradient(135deg, #f39c12, #e74c3c, #e84393);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 12px 0 24px;
  animation: praiseIn 0.8s ease;
}

@keyframes praiseIn {
  0% { transform: translateY(20px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}

.stars-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.stars-row {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
}

.star {
  display: inline-block;
  font-size: 40px;
  opacity: 0;
  animation: starPop 0.5s ease forwards;
}

.star-bonus {
  font-size: 32px;
}

@keyframes starPop {
  0% { transform: scale(0) rotate(-180deg); opacity: 0; }
  60% { transform: scale(1.3) rotate(10deg); opacity: 1; }
  80% { transform: scale(0.9) rotate(-5deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

.stars-legend {
  display: flex;
  gap: 16px;
  justify-content: center;
  font-size: 13px;
  color: #aaa;
  flex-wrap: wrap;
}

.results-details {
  width: 100%;
  max-width: 500px;
  border-top: 1px solid #eee;
  padding-top: 20px;
  margin-bottom: 16px;
}

.results-stats {
  text-align: center;
  margin-bottom: 16px;
}

.results-stats p {
  font-size: 16px;
  color: #999;
  margin-bottom: 4px;
}

.results-stats strong {
  color: #777;
}

.results-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 20px;
}

.btn-home {
  padding: 14px 32px;
  background: #f0f0f0;
  color: #555;
  border-radius: 12px;
  text-decoration: none;
  font-size: 18px;
  font-weight: 700;
  transition: background 0.2s;
}

.btn-home:hover {
  background: #e0e0e0;
}
</style>
